import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { Button, CircularProgress, Typography, Box, Grid } from "@material-ui/core";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PercentIcon from "@mui/icons-material/Percent";
import LayersIcon from "@mui/icons-material/Layers";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { showErrorToast } from "../../../../common/components/Helper/Utils";
import { profitModal } from "./../../dealer/actions/dealerAction";
import '../style/style.css';

const VALUE_CLASS = {
  gold: "profit-summary-value-gold",
  green: "profit-summary-value-green",
  red: "profit-summary-value-red",
  blue: "profit-summary-value-blue",
};

function StatCard({ icon, iconBg, iconColor, label, value, valueClass, active, extra }) {
  return (
    <Box className={`profit-summary-card${active ? " profit-summary-card-highlight" : ""}`}>
      <Box className="profit-summary-card-header">
        <Box className="profit-summary-card-header-left">
          <Box
            className="profit-summary-icon"
            style={{ backgroundColor: iconBg, color: iconColor }}
          >
            {icon}
          </Box>
          <Typography className="profit-summary-label">{label}</Typography>
        </Box>
        {extra}
      </Box>
      <div className={`profit-summary-value ${VALUE_CLASS[valueClass] || ""}`}>{value}</div>
    </Box>
  );
}

const formatProfitAmount = (value) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    return "-";
  }
  return `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  })}`;
};

const formatProfitPercentage = (value) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    return "-";
  }
  return `${Number(value).toFixed(2)}%`;
};

export default function ProfitModal(props) {
  const { open, handleClose, invoice, invoices, filterPayload } = props;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorText, setErrorText] = useState('');
  const [showInvoiceIds, setShowInvoiceIds] = useState(false);
  const getProfitColorScheme = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return { valueClass: "green", iconBg: "#e8f5e9", iconColor: "#2e7d32" };
    }
    if (Number(value) < 0) {
      return { valueClass: "red", iconBg: "#fdecea", iconColor: "#c62828" };
    }
    return { valueClass: "green", iconBg: "#e8f5e9", iconColor: "#2e7d32" };
  };

  useEffect(() => {
    if (!open) return;

    const invoiceIds = invoices && invoices.length
      ? invoices.map((item) => item.id)
      : invoice
      ? [invoice.id]
      : [];

    if (invoiceIds.length === 0) {
      setErrorText('No invoice selected.');
      return;
    }

    const payload = {
      ...(filterPayload || {}),
      invoiceIds,
    };

    setLoading(true);
    setErrorText('');
    setResult(null);

    profitModal(payload, (error, response) => {
      setLoading(false);
      if (error || !response?.data?.data) {
        const msg = error?.response?.data?.message || 'Failed to calculate profit.';
        setErrorText(msg);
        showErrorToast(msg);
        return;
      }
      setResult(response.data.data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalInvoiceAmount = result ? Number(result.totalprice) : null;
  const invoiceCount = result ? Number(result.invoicecount) : null;
  const profitDifference = result ? Number(result.profitdifference) : null;
  const profitPercentage = result ? Number(result.profitpercentage) : null;
  const invoiceIdsList = result?.id || [];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth={true}
      maxWidth={'sm'}
    >
      <DialogTitle>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            <Box
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                backgroundColor: '#fdf1d8',
                color: '#c9971c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TrendingUpIcon />
            </Box>
            <Box>
              <Typography id="payback-modal-title" variant="h6" component="h2">
                Profit Summary
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => { handleClose(); }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && errorText && (
          <Typography color="error">{errorText}</Typography>
        )}

        {!loading && !errorText && result && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <StatCard
                icon={<AttachMoneyIcon fontSize="small" />}
                iconBg="#fff4df"
                iconColor="#c68642"
                label="TOTAL INVOICE AMOUNT"
                value={formatProfitAmount(totalInvoiceAmount)}
                valueClass="gold"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StatCard
                icon={<LayersIcon fontSize="small" />}
                iconBg="#e8f1fb"
                iconColor="#1976d2"
                label="NO. OF INVOICES"
                value={invoiceCount ?? "-"}
                valueClass="blue"
                active={showInvoiceIds}
                extra={
                  invoiceIdsList.length > 0 && (
                    <Box
                      className="profit-summary-toggle-ids"
                      onClick={() => setShowInvoiceIds((prev) => !prev)}
                    >
                      {showInvoiceIds ? "Hide IDs" : "Show IDs"}
                      {showInvoiceIds ? (
                        <KeyboardArrowUpIcon className="profit-summary-toggle-icon profit-summary-toggle-icon-up" />
                      ) : (
                        <KeyboardArrowDownIcon className="profit-summary-toggle-icon" />
                      )}
                    </Box>
                  )
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StatCard
                icon={<TrendingUpIcon fontSize="small" />}
                iconBg="#e8f5e9"
                iconColor="#2e7d32"
                label="PROFIT"
                value={formatProfitAmount(profitDifference)}
                valueClass={getProfitColorScheme(profitDifference).valueClass}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StatCard
                icon={<PercentIcon fontSize="small" />}
                iconBg="#e8f5e9"
                iconColor="#2e7d32"
                label="PROFIT %"
                value={formatProfitPercentage(profitPercentage)}
                valueClass={getProfitColorScheme(profitPercentage).valueClass}
              />
            </Grid>

            {showInvoiceIds && invoiceIdsList.length > 0 && (
              <Grid item xs={12}>
                <Box className="profit-summary-ids-panel">
                  <div className="profit-summary-ids-title">
                    SELECTED INVOICE IDS
                  </div>
                  <Typography className="profit-summary-ids-list">
                    {invoiceIdsList.join(", ")}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={handleClose}
          style={{
            background: "#fff",
            color: "#000",
            marginTop: "20px",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}